import { ConditionReferenceCodeDisplay } from '@chiroup/core/types/PatientVisit.type';
import { ReferenceCodeDisplay } from '@chiroup/core/types/ReferenceCode.type';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React from 'react';

type Props = {
  parentId: number;
  name: string;
  image: string | null;
  conditionId: number;
  remove?: () => void;
  onChange: (
    val: number,
    data: {
      regions: string[];
      muscles: number[];
      typicallyNegativeEvals: number[];
      typicallyPositiveEvals: number[];
      referenceCodes: ReferenceCodeDisplay[];
    }
  ) => void;
  conditionIdSet: Set<number>;
  handleDeleteCondition: (complaintIndex: number, index: number) => void;
  complaintIndex: number;
  selectedConditions: ConditionReferenceCodeDisplay[] | undefined;
  regions: string[];
  typicallyPositiveEvals: number[];
  typicallyNegativeEvals: number[];
  muscles: number[];
  toggleFavorite: (id: number) => void;
  favorite: number;
  referenceCodes: ReferenceCodeDisplay[];
};

const NotesCondition: React.FC<Props> = ({
  parentId,
  name,
  image,
  conditionId,
  regions = [],
  onChange,
  conditionIdSet,
  handleDeleteCondition,
  complaintIndex,
  selectedConditions,
  typicallyPositiveEvals = [],
  typicallyNegativeEvals = [],
  muscles = [],
  toggleFavorite,
  favorite = 0,
  referenceCodes = [],
}) => {
  const handleClick = () => {
    if (!conditionIdSet.has(conditionId)) {
      onChange(conditionId, {
        regions,
        typicallyPositiveEvals,
        typicallyNegativeEvals,
        muscles,
        referenceCodes,
      });
    } else {
      handleDeleteCondition(
        complaintIndex,
        selectedConditions?.findIndex(c => c.conditionId === conditionId) || 0
      );
    }
  };

  const listItemClass = `flex min-h-16 shadow-sm rounded-md select-none cursor-pointer col-span-1 ${
    conditionIdSet.has(conditionId) ? 'opacity-100' : 'opacity-40'
  } hover:opacity-100`;

  return (
    <li className={listItemClass} onClick={handleClick}>
      {image ? (
        // self-stretch + object-cover (not a fixed h-16): when a long name or
        // the selected check wraps the card taller, the image and its borders
        // must track the card height instead of detaching from it.
        <img
          src={image}
          className="shrink-0 w-16 self-stretch object-cover rounded-l-md border-l border-t border-b border-gray-300 dark:border-dark-gray-800"
          alt={name}
        />
      ) : (
        <div className="shrink-0 flex items-center justify-center w-16 bg-primary-500 text-white text-sm font-medium rounded-l-md">
          {name?.slice(0, 2).toUpperCase()}
        </div>
      )}
      <div className="flex-1 flex items-center justify-between border-t border-r border-b border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 rounded-r-md">
        <div className="flex-1 px-4 py-2 text-sm ">
          <div className="flex items-center gap-4">
            <span title={name}>{name}</span>{' '}
            {conditionIdSet.has(conditionId) && (
              <Icon icon={Icons.checkCircle} className="h-8 w-8 shrink-0 text-primary-500 mr-2" />
            )}
          </div>
        </div>
        <div>
          <svg
            className="h-8 w-8 text-pink-400 cursor-pointer mr-1"
            xmlns="http://www.w3.org/2000/svg"
            fill={favorite ? 'currentColor' : 'none'}
            viewBox="0 0 24 24"
            stroke="currentColor"
            onClick={e => {
              toggleFavorite(parentId);
              e?.stopPropagation();
            }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </div>
      </div>
    </li>
  );
};

export default NotesCondition;
